import "jsoneditor-react/es/editor.min.css";
import { ControlledJsonEditor } from "./UserDetailsSpace";
import { VisualJsonBuilder, VisualJsonBuilderRef } from "./VisualJsonBuilder";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import useAuth from "../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../../public/ConfirmModal";
import { CustomButton } from "../../atoms/CustomButton";
import {
  HiArrowRight,
  HiChevronDown,
  HiInformationCircle,
  HiOutlineCloudUpload,
  HiOutlineExclamationCircle,
  HiOutlineViewGrid,
  HiViewGrid,
} from "react-icons/hi";
import UpgradeModal from "../../modals/UpgradeModal";
import { PremiumBenefit } from "../../atoms/UpgradePromptContent";
import {
  useCreateUserPreferencesWithSpecialKey,
  useDeleteUserPreferenceField,
  useDeleteUserPreferencesWithSpecialKey,
  useGetUserPreferences,
  useGetUserPreferencesWithSpecialKey,
  useRenameUserPreferencesWithSpecialKey,
  useUpdateUserPreferencesWithSpecialKey,
} from "../../../hooks/users";
import parse from "html-react-parser";
import { FolderHeader } from "../../atoms/FolderHeader";
import { FolderGrid, RESERVED_FOLDER_KEYS } from "../../atoms/FolderGrid";
import { useFolderFieldSpace } from "../../../hooks/useFolderFieldSpace";
import { NavigationHeader } from "../../atoms/NavigationHeader";
import { NewFolderForm } from "../../atoms/NewFolderForm";
import { FieldsGrid } from "../../atoms/FieldsGrid";
import { KeyValueTable } from "../../atoms/KeyValueTable";
import { CreateFieldPrompt } from "../../atoms/CreateFieldPrompt";
import { usePermissions } from "../../../utils/permissions";
import { Cap, Level } from "../../../utils/rbacCatalog";

interface UserFilesSpaceProps {
  customerId: string;
}

const UserFilesSpace: React.FC<UserFilesSpaceProps> = ({ customerId }) => {
  const { t } = useTranslation();
  const { customer_identification, user, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const isSelf = user.user_identification === customerId;
  const canWriteFiles = isSelf || permissions.can(Cap.USERS_FILES, Level.WRITE);
  const canDeleteFiles =
    isSelf || permissions.can(Cap.USERS_FILES, Level.DELETE);

  const visualJsonBuilderRef = useRef<VisualJsonBuilderRef>(null);
  const [isHelpExpanded, setIsHelpExpanded] = useState(false);

  const {
    updatedData,
    setUpdatedData,
    isNewKeyProcess,
    setIsNewKeyProcess,
    selectOptionValue,
    setSelectOptionValue,
    showConfirm,
    setShowConfirm,
    editorMode,
    setEditorMode,
    currentView,
    setCurrentView,
    selectedFolder,
    setSelectedFolder,
    selectedField,
    setSelectedField,
    isNewFieldProcess,
    setIsNewFieldProcess,
    showDeleteFieldConfirm,
    setShowDeleteFieldConfirm,
    showFieldLimitModal,
    setShowFieldLimitModal,
    folderFieldCounts,
    setFolderFieldCounts,
    control,
    setValue,
    inputTextValue,
    tempFieldNameRef,
    originalFieldDataRef,
    maxLimit,
    largeBusinessTierUser,
    handleOpenFolder,
    handleBackToFolders,
    handleOpenField,
    handleBackToFolder,
    handleCreateNewFolder,
    handleCreateNewField,
    handleFieldChange,
    validateFolderName,
    cleanAndPrepareData,
    sanitizeInput,
    isReservedFolder,
  } = useFolderFieldSpace({
    user,
    reservedFolderKeys: RESERVED_FOLDER_KEYS,
    translationPrefix: "dashboard.UserFilesSpace",
  });

  const [showFileUpgradeModal, setShowFileUpgradeModal] = React.useState(false);

  const isScalarFolderValue = (value: unknown): boolean =>
    value === null ||
    (typeof value !== "object" && typeof value !== "undefined") ||
    Array.isArray(value);

  const [scalarFolder, setScalarFolder] = React.useState<{
    key: string;
    value: unknown;
  } | null>(null);

  const { data, isLoading, isError, isSuccess, refetch } =
    useGetUserPreferences(
      customer_identification,
      user.entity_identification,
      customerId
    );
  const { mutate: getUserData, isSuccess: isSuccessGetData } =
    useGetUserPreferencesWithSpecialKey();
  const { mutate: updateUserData, isLoading: isUpdating } =
    useUpdateUserPreferencesWithSpecialKey();
  const { mutate: createUserData } = useCreateUserPreferencesWithSpecialKey();
  const { mutate: deleteUserData, isLoading: isDeleting } =
    useDeleteUserPreferencesWithSpecialKey();
  const { mutate: deleteUserPreferenceField, isLoading: isDeletingField } =
    useDeleteUserPreferenceField();
  const { mutate: renameUserData, isLoading: isRenaming } =
    useRenameUserPreferencesWithSpecialKey();

  const fileUploadBenefits: PremiumBenefit[] = [
    {
      id: "files-upload-premium-1",
      text: t("dashboard.upgrade.fileUpload.benefit1") as string,
    },
    {
      id: "files-upload-premium-2",
      text: t("dashboard.upgrade.fileUpload.benefit2") as string,
    },
    {
      id: "files-upload-premium-3",
      text: t("dashboard.upgrade.fileUpload.benefit3") as string,
    },
  ];

  const fieldLimitBenefits: PremiumBenefit[] = [
    {
      id: "files-field-limit-premium-1",
      text: t("dashboard.upgrade.subfolders.benefit1") as string,
    },
    {
      id: "files-field-limit-premium-2",
      text: t("dashboard.upgrade.subfolders.benefit2") as string,
    },
    {
      id: "files-field-limit-premium-3",
      text: t("dashboard.upgrade.subfolders.benefit3") as string,
    },
  ];

  useEffect(() => {
    if (currentView === "folders" || selectOptionValue === "") {
      setScalarFolder(null);
    }
  }, [currentView, selectOptionValue]);

  useEffect(() => {
    if (selectOptionValue !== "") {
      getUserData(
        {
          customer_identification,
          entity_identification: user.entity_identification,
          customerId,
          key: selectOptionValue,
        },
        {
          onSuccess: (data, variables) => {
            const rawValue =
              variables === ""
                ? {}
                : data?.data?.element?.user_data_preferences[variables.key] ??
                  {};

            if (isScalarFolderValue(rawValue)) {
              setScalarFolder({ key: variables.key, value: rawValue });
              setUpdatedData({});
              setFolderFieldCounts((prev) => ({
                ...prev,
                [selectOptionValue]: 1,
              }));
              return;
            }

            setScalarFolder(null);
            const folderData = rawValue as Record<string, any>;
            setUpdatedData(folderData);
            setFolderFieldCounts((prev) => ({
              ...prev,
              [selectOptionValue]: Object.keys(folderData).length,
            }));
          },
        }
      );
    } else {
      setUpdatedData({});
    }
  }, [
    selectOptionValue,
    customer_identification,
    customerId,
    getUserData,
    user.entity_identification,
    setUpdatedData,
    setFolderFieldCounts,
  ]);

  useEffect(() => {
    if (
      currentView === "folders" &&
      data?.user_data_preferences &&
      data.user_data_preferences.length > 0
    ) {
      try {
        const nextCounts: Record<string, number> = {};

        if (
          data?.data?.element?.user_data_preferences &&
          typeof data.data.element.user_data_preferences === "object"
        ) {
          const payload = data.data.element.user_data_preferences;
          Object.keys(payload).forEach((k) => {
            nextCounts[k] = isScalarFolderValue(payload[k])
              ? 1
              : Object.keys(payload[k] ?? {}).length;
          });
        }

        setFolderFieldCounts((prev) => ({ ...prev, ...nextCounts }));

        const needFetch = data.user_data_preferences.filter(
          (k: string) =>
            nextCounts[k] === undefined &&
            !(
              data?.data?.element?.user_data_preferences &&
              data.data.element.user_data_preferences[k]
            )
        );

        if (needFetch.length > 0) {
          const fetchSequentially = async () => {
            const counts: Record<string, number> = {};

            for (const folderKey of needFetch) {
              try {
                const response: any = await new Promise((resolve, reject) => {
                  getUserData(
                    {
                      customer_identification,
                      entity_identification: user.entity_identification,
                      customerId,
                      key: folderKey,
                    },
                    { onSuccess: resolve, onError: reject }
                  );
                });

                const rawValue =
                  response?.data?.element?.user_data_preferences[folderKey] ??
                  {};
                counts[folderKey] = isScalarFolderValue(rawValue)
                  ? 1
                  : Object.keys(rawValue).length;
              } catch (error) {
                console.error(`Error fetching folder "${folderKey}":`, error);
              }
            }

            setFolderFieldCounts((prev) => ({ ...prev, ...counts }));
          };

          fetchSequentially();
        }
      } catch (err) {
        console.error("Error parsing folder counts:", err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, data?.user_data_preferences]);

  const handleUpdateData = async (fieldData: Record<string, any>) => {
    if (!canWriteFiles) return;
    const allFieldsData =
      currentView === "field" ? { ...updatedData, ...fieldData } : fieldData;
    const cleanedData = cleanAndPrepareData(allFieldsData);

    if (isNewKeyProcess && inputTextValue) {
      const existingFolders = data?.user_data_preferences || [];
      if (!validateFolderName(inputTextValue, existingFolders)) return;
    }

    const savePromise = new Promise<void>((resolve, reject) => {
      if (isNewKeyProcess && inputTextValue) {
        createUserData(
          {
            customer_identification,
            entity_identification: user.entity_identification,
            customerId,
            key: inputTextValue,
            data: cleanedData,
          },
          { onSuccess: () => resolve(), onError: (error) => reject(error) }
        );
      } else if (!isNewKeyProcess && selectOptionValue) {
        updateUserData(
          {
            customer_identification,
            entity_identification: user.entity_identification,
            customerId,
            key: selectOptionValue,
            data: cleanedData,
          },
          { onSuccess: () => resolve(), onError: (error) => reject(error) }
        );
      } else {
        reject(new Error("Invalid state"));
      }
    });

    await savePromise;

    tempFieldNameRef.current = "";
    originalFieldDataRef.current = {};

    if (currentView === "field") {
      const folderKeyToFetch = isNewKeyProcess
        ? inputTextValue
        : selectOptionValue;

      if (folderKeyToFetch) {
        getUserData(
          {
            customer_identification,
            entity_identification: user.entity_identification,
            customerId,
            key: folderKeyToFetch,
          },
          {
            onSuccess: (data, variables) => {
              setUpdatedData(
                variables === ""
                  ? {}
                  : data?.data?.element?.user_data_preferences[variables.key] ??
                      {}
              );

              if (isNewKeyProcess) {
                setValue("newKeyName", "");
                setSelectOptionValue("");
                setSelectedFolder("");
                setIsNewKeyProcess(false);
                setUpdatedData({});
                setSelectedField("");
                setIsNewFieldProcess(false);
                setCurrentView("folders");
                setEditorMode("simple");
                refetch();
              } else {
                handleBackToFolder();
              }
            },
          }
        );
      } else {
        handleBackToFolder();
      }

      toast.success(t("dashboard.UserFilesSpace.fieldSavedSuccess"));
    } else {
      if (isNewKeyProcess) {
        setValue("newKeyName", "");
        setSelectOptionValue("");
        setSelectedFolder("");
        setIsNewKeyProcess(false);
        setUpdatedData({});
        setSelectedField("");
        setIsNewFieldProcess(false);
        setCurrentView("folders");
        setEditorMode("simple");
        toast.success(t("dashboard.UserFilesSpace.folderCreatedSuccess"));
        refetch();
      } else {
        setValue("newKeyName", "");
        setSelectOptionValue("");
        setIsNewKeyProcess(false);
        handleBackToFolders();
        toast.success(t("dashboard.UserFilesSpace.folderSavedSuccess"));
        refetch();
      }
    }
  };

  const handleDeleteFolder = () => {
    if (!canDeleteFiles) return;
    deleteUserData(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        customerId,
        key: selectOptionValue,
      },
      {
        onSuccess: () => {
          setValue("newKeyName", "");
          setSelectOptionValue("");
          setIsNewKeyProcess(false);
          setUpdatedData({});
          setShowConfirm(false);
          handleBackToFolders();
        },
      }
    );
  };

  const handleDeleteField = () => {
    if (!canDeleteFiles) return;
    if (!selectedField || !selectOptionValue) return;

    deleteUserPreferenceField(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        customerId,
        folder: selectOptionValue,
        field_key: selectedField,
      },
      {
        onSuccess: () => {
          const newData = { ...updatedData };
          delete newData[selectedField];
          setUpdatedData(newData);
          originalFieldDataRef.current = {};
          setShowDeleteFieldConfirm(false);
          setSelectedField("");
          setCurrentView("folder");
          setIsNewFieldProcess(false);
          refetch();
        },
        onError: () => setShowDeleteFieldConfirm(false),
      }
    );
  };

  const onUpdateFolderName = (newFolderName: string) => {
    const existingFolders = data?.user_data_preferences || [];
    if (!validateFolderName(newFolderName, existingFolders)) return;

    renameUserData(
      {
        customer_identification,
        entity_identification: user.entity_identification,
        customerId,
        key: selectedFolder,
        data: updatedData,
        new_key: newFolderName,
      },
      {
        onSuccess: () => {
          setSelectedFolder(newFolderName);
          setSelectOptionValue(newFolderName);
          toast.success(
            t("dashboard.UserFilesSpace.folderRenamedSuccess", {
              folderName: newFolderName,
            })
          );
        },
        onError: () => {
          toast.error(t("dashboard.UserFilesSpace.folderRenameFailed"));
        },
      }
    );
  };

  let content;

  if (isLoading) {
    content = <p>{t("dashboard.UserFilesSpace.loading")}</p>;
  }

  if (isError) {
    content = (
      <CustomButton
        variant="primary"
        onClick={() => refetch()}
        title={t("dashboard.UserDetailsSpace.btn-01") as string}
      />
    );
  }

  if (isSuccess) {
    content = (
      <React.Fragment>
        {currentView === "folders" ? (
          <FolderGrid
            folders={data?.user_data_preferences || []}
            folderFieldCounts={folderFieldCounts}
            onOpenFolder={handleOpenFolder}
            onDeleteFolder={(folderKey) => {
              if (!canDeleteFiles) return;
              setSelectOptionValue(folderKey);
              setSelectedFolder(folderKey);
              setShowConfirm(true);
            }}
            onCreateFolder={() => {
              if (!canWriteFiles) return;
              handleCreateNewFolder(data?.user_data_preferences || []);
            }}
            canCreate={canWriteFiles}
            canDelete={canDeleteFiles}
            showFieldCount={true}
          />
        ) : currentView === "folder" ? (
          <div className="space-y-4">
            <NavigationHeader
              onBack={handleBackToFolders}
              backLabel={t("dashboard.UserFilesSpace.backToFolders") as string}
              showDelete={
                canDeleteFiles &&
                !isNewKeyProcess &&
                !!selectedFolder &&
                !isReservedFolder(selectedFolder) &&
                !scalarFolder
              }
              onDelete={() => setShowConfirm(true)}
              deleteLabel={t("dashboard.UserFilesSpace.deleteFolder") as string}
            />

            {isNewKeyProcess ? (
              <NewFolderForm
                control={control}
                value={inputTextValue}
                onValueChange={(value) =>
                  setValue("newKeyName", sanitizeInput(value))
                }
                title={t("dashboard.UserFilesSpace.createNewFolder") as string}
                label={t("dashboard.UserFilesSpace.folderName") as string}
                placeholder={
                  t("dashboard.UserFilesSpace.createPlaceholder") as string
                }
              />
            ) : (
              <FolderHeader
                folderName={selectedFolder}
                onUpdate={
                  isReservedFolder(selectedFolder) || scalarFolder
                    ? undefined
                    : onUpdateFolderName
                }
                isUpdating={
                  isUpdating || isDeleting || isRenaming || isDeletingField
                }
              />
            )}

            {selectOptionValue &&
              isSuccessGetData &&
              (scalarFolder ? (
                <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                  <KeyValueTable
                    fields={{ [scalarFolder.key]: scalarFolder.value }}
                    onEditField={() => {}}
                    onDeleteField={() => {}}
                    onCreateField={() => {}}
                    maxLimit={maxLimit}
                    isLargeBusinessTier={largeBusinessTierUser}
                    isReadOnlyFolder={true}
                    readOnlyTooltip={
                      t("dashboard.UserFilesSpace.readOnlyValue") as string
                    }
                  />
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900 text-lg">
                      {t("dashboard.UserFilesSpace.fields")}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {largeBusinessTierUser
                        ? t("dashboard.UserFilesSpace.field", {
                            count: Object.keys(updatedData).length,
                          })
                        : t("dashboard.UserFilesSpace.fieldsLimit", {
                            count: Object.keys(updatedData).length,
                            max: maxLimit,
                          })}
                    </p>
                  </div>
                  <FieldsGrid
                    fields={updatedData}
                    onEditField={handleOpenField}
                    onDeleteField={
                      canDeleteFiles
                        ? (fieldKey) => {
                            setSelectedField(fieldKey);
                            setShowDeleteFieldConfirm(true);
                          }
                        : undefined
                    }
                    onCreateField={() => {
                      if (!canWriteFiles) return;
                      handleCreateNewField(data?.user_data_preferences);
                    }}
                    maxLimit={maxLimit}
                    isLargeBusinessTier={largeBusinessTierUser}
                    onUpgradeClick={() => setShowFieldLimitModal(true)}
                    isReadOnlyFolder={
                      isReservedFolder(selectedFolder) || !canWriteFiles
                    }
                    readOnlyTooltip={
                      t("dashboard.FolderGrid.reservedFolder") as string
                    }
                  />
                </div>
              ))}

            {isNewKeyProcess && inputTextValue && (
              <CreateFieldPrompt
                onCreateField={() =>
                  handleCreateNewField(data?.user_data_preferences)
                }
                title={t("dashboard.UserFilesSpace.addField") as string}
                subtitle={
                  t("dashboard.UserFilesSpace.createFirstField") as string
                }
              />
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <NavigationHeader
              onBack={handleBackToFolder}
              backLabel={t("dashboard.UserFilesSpace.backToFields") as string}
              showDelete={
                canDeleteFiles &&
                !isNewFieldProcess &&
                !!selectedField &&
                !isReservedFolder(selectedFolder)
              }
              onDelete={() => setShowDeleteFieldConfirm(true)}
              deleteLabel={t("dashboard.UserFilesSpace.deleteField") as string}
            />

            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 text-lg mb-4">
                {isNewFieldProcess ? (
                  t("dashboard.UserFilesSpace.createNewFieldTitle")
                ) : (
                  <>
                    {t("dashboard.UserFilesSpace.editFieldLabel")}{" "}
                    <span className="text-base font-normal text-gray-600">
                      {selectedField}
                    </span>
                  </>
                )}
              </h3>

              <div translate="no">
                {editorMode === "simple" ? (
                  <VisualJsonBuilder
                    ref={visualJsonBuilderRef}
                    initialValue={
                      isNewFieldProcess
                        ? { "": "" }
                        : { [selectedField]: updatedData[selectedField] }
                    }
                    onChange={(newValue) =>
                      handleFieldChange(newValue, isNewFieldProcess)
                    }
                    mode={editorMode}
                    onModeChange={setEditorMode}
                    maxFileLimit={maxLimit}
                    onUpgradeClick={
                      largeBusinessTierUser
                        ? undefined
                        : () => setShowFileUpgradeModal(true)
                    }
                    allowedTypes={["file"]}
                    onSave={handleUpdateData}
                    onCancel={handleBackToFolder}
                    isSaving={isUpdating}
                    userEndedSubscription={userEndedSubscription}
                    showActionButtons={true}
                    viewMode="files"
                    readOnlyFieldName={isReservedFolder(selectedFolder)}
                    requireFiles={isNewFieldProcess}
                    preferencesFolder={selectOptionValue}
                    userId={customerId}
                  />
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-end">
                      <button
                        onClick={() => setEditorMode("simple")}
                        className="group flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
                      >
                        <HiViewGrid className="w-4 h-4 transition-transform group-hover:scale-110" />
                        <span>
                          {t("dashboard.UserFilesSpace.switchToSimpleView")}
                        </span>
                        <HiArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                    <ControlledJsonEditor
                      value={
                        isNewFieldProcess
                          ? { "": "" }
                          : { [selectedField]: updatedData[selectedField] }
                      }
                      onChange={(newValue) =>
                        handleFieldChange(newValue, isNewFieldProcess)
                      }
                    />
                    <div className="flex justify-end mt-4 gap-2">
                      <button
                        onClick={handleBackToFolder}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                      >
                        {t("common.cancel")}
                      </button>
                      <CustomButton
                        onClick={() => {
                          const jsonObject = isNewFieldProcess
                            ? { "": "" }
                            : { [selectedField]: updatedData[selectedField] };
                          handleUpdateData(jsonObject);
                        }}
                        disabled={
                          isUpdating ||
                          userEndedSubscription ||
                          !updatedData ||
                          !canWriteFiles
                        }
                        isLoading={isUpdating}
                        variant="primary"
                        icon="save"
                        title={t("common.saveShort")}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <ConfirmModal
          cancelBtnName={t("dashboard.UserDetailsSpace.key3")}
          cancelBtnFn={() => setShowConfirm(false)}
          onClose={() => setShowConfirm(false)}
          confirmBtnFn={handleDeleteFolder}
          show={showConfirm}
          isLoading={isDeleting}
          ConfirmBtnColor="red"
          confirmBtnName={t("dashboard.UserDetailsSpace.key1")}
          title={t("dashboard.UserDetailsSpace.key6", {
            folderName:
              selectedFolder.length > 20
                ? selectedFolder.slice(0, 20) + "..."
                : selectedFolder,
          })}
          description={t("dashboard.UserDetailsSpace.key7")}
          icon={
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
          }
          iconStyle="text-center"
        />

        <ConfirmModal
          cancelBtnName={t("common.cancel")}
          cancelBtnFn={() => setShowDeleteFieldConfirm(false)}
          onClose={() => setShowDeleteFieldConfirm(false)}
          confirmBtnFn={handleDeleteField}
          show={showDeleteFieldConfirm}
          isLoading={isDeletingField}
          ConfirmBtnColor="red"
          confirmBtnName={t("dashboard.UserDetailsSpace.key1")}
          title={t("dashboard.UserFilesSpace.deleteFieldTitle", {
            fieldName:
              selectedField.length > 20
                ? selectedField.slice(0, 20) + "..."
                : selectedField,
          })}
          description={t("dashboard.UserFilesSpace.deleteFieldConfirm")}
          icon={
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
          }
          iconStyle="text-center"
        />

        <UpgradeModal
          showModal={showFileUpgradeModal}
          setShowModal={setShowFileUpgradeModal}
          title={t("dashboard.upgrade.fileUpload.premiumTitle") as string}
          description={
            t("dashboard.upgrade.fileUpload.premiumDescription") as string
          }
          benefits={fileUploadBenefits}
          icon={HiOutlineCloudUpload}
          iconTheme="blue"
          upgradeText={t("dashboard.upgrade.key3") as string}
        />

        <UpgradeModal
          showModal={showFieldLimitModal}
          setShowModal={setShowFieldLimitModal}
          title={t("dashboard.upgrade.subfolders.premiumTitle") as string}
          description={
            t("dashboard.upgrade.subfolders.premiumDescription") as string
          }
          benefits={fieldLimitBenefits}
          icon={HiOutlineViewGrid}
          iconTheme="blue"
          upgradeText={t("dashboard.upgrade.key3") as string}
        />
      </React.Fragment>
    );
  }

  return (
    <div className="w-full edit-preferences p-0">
      <div className="mb-4">
        <div className="mt-3 border border-blue-200 rounded-lg overflow-hidden bg-blue-50">
          <button
            type="button"
            onClick={() => setIsHelpExpanded((v) => !v)}
            className="w-full flex items-center justify-between p-3 hover:bg-blue-100 transition-colors"
            aria-expanded={isHelpExpanded}
          >
            <div className="flex items-center gap-2 text-left">
              <HiInformationCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-blue-900">
                {t("dashboard.UserFilesSpace.help-title")}
              </h3>
            </div>
            <HiChevronDown
              className={`text-blue-600 transition-transform flex-shrink-0 ${
                isHelpExpanded ? "rotate-180" : ""
              }`}
              size={20}
            />
          </button>
          {isHelpExpanded && (
            <div className="p-3 pt-0 border-t border-blue-200">
              <p className="text-sm text-blue-900 whitespace-pre-line pt-3">
                {parse(t("dashboard.UserFilesSpace.help-text"))}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="block relative">{content}</div>
    </div>
  );
};

export default UserFilesSpace;
